import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom, map } from 'rxjs';
import { IBooking, IBookingAddOn } from '@cnt-workspace/models';
import type { IOrder } from '@curbnturf/entities';
import { ReservationApiService } from '../backend-api/reservation-api.service';
import { ToastService } from '../toast/toast.service';
import { AuthService } from '../auth/auth.service';
import { readPublishedSnapshot } from '../listings/published-snapshot.util';
import { getActiveRvProfile } from '../my-rv.util';
import {
  addOnsToOrderExtras, addOnsToReservationExtras, buildReservationCreatePayload, isoRangeToCDate,
  priceSummaryFrom, roundMoney, toBookingView,
} from './reservation-real-mapper';
import { PriceDisplay } from '@curbnturf/entities';
import { createIdbKvStore } from '../storage/idb-kv-store.util';
import { isoKey, parseBookingDate } from '../shared/iso-date.util';

const BOOKINGS_KEY = 'cnt-bookings';
const BOOKINGS_IDB_KEY = 'booking:mock-bookings';

/** Dollars credited per reviewed night. Centralized so every copy + formula stays in sync. */
export const REVIEW_CREDIT_PER_NIGHT = 5;
/** Minimum trimmed review text length required to actually earn the credit. */
export const MIN_REVIEW_CHARS_FOR_CREDIT = 20;

/** One row in the user's credit ledger — drives the breakdown disclosure on /dashboard. */
export interface ICreditEntry {
  type: 'earned' | 'spent';
  amount: number;
  bookingId: string;
  listingTitle: string;
  date: string;       // ISO
}

/**
 * Bookings for listings the host actually published against the real
 * backend (see `host-listing-draft.service.ts`'s `realSiteId` snapshot
 * field) go through the real `/reservation` endpoints via `ReservationService`
 * — create/cancel/accept/reject/change — and are cached in `_realBookings$`,
 * keyed by a `res-<reservationId>` id so mutating calls know to route real.
 * Everything else (the seed mock catalog, which is the vast majority of
 * listings — search/POI discovery stays mock this pass) keeps the original
 * localStorage-backed behavior in `_bookings$` completely unchanged.
 *
 * Public methods merge both sources so every existing read-only consumer
 * (trips, dashboard, notifications, availability, messaging) keeps working
 * against one flat `IBooking[]` regardless of which backing store a given
 * booking came from.
 *
 * Known, deliberate gaps for real bookings (no backend equivalent exists —
 * see the Phase 4 research doc):
 *  - `qualifiesForCredit`/`getAvailableCredit`/`getCreditHistory`/
 *    `markReviewed` stay mock-only. Real reward credit ("CurbNTurf Cash") is
 *    earned via the real rating/review flow, which isn't wired until
 *    reviews/ratings are converged onto `@curbnturf/rating` (out of scope
 *    for this pass). `markHostReviewed` is real for real bookings — it just
 *    locally patches `hostReviewedAt` (the actual reveal state still comes
 *    from the server via `reservation.guestRated`); it doesn't touch reward
 *    credit either.
 *
 * Mock "pending" bookings wait for a genuine manual host decision
 * (`hostDecide()`) just like real ones do — there is no auto-decision timer.
 */
/** Server price for a proposed booking change (see `BookingService.quoteModify`). */
export interface ModifyQuote {
  nightsSubtotal: number;
  extrasTotal: number;
  discounts: { label: string; amount: number }[];
  taxTotal: number;
  /** New booking total, before any CurbNTurf Cash. */
  total: number;
  /** `total` minus the current booking total — positive means the guest owes more. */
  difference: number;
}

@Injectable({ providedIn: 'root' })
export class BookingService {
  private platformId = inject<object>(PLATFORM_ID);
  private toasts = inject(ToastService);
  private auth = inject(AuthService);
  private reservationSvc = inject(ReservationApiService);

  private readonly store = createIdbKvStore<IBooking[]>({
    legacyKeys: { [BOOKINGS_IDB_KEY]: BOOKINGS_KEY },
  });

  private readonly _bookings$ = new BehaviorSubject<IBooking[]>([]);
  private readonly _realBookings$ = new BehaviorSubject<IBooking[]>([]);
  readonly bookings$: Observable<IBooking[]> = combineLatest([this._bookings$, this._realBookings$]).pipe(
    map(([mock, real]) => [...mock, ...real]),
  );

  constructor() {
    const initial = this.read();
    this._bookings$.next(initial);
    if (isPlatformBrowser(this.platformId)) {
      // read() above likely returns [] pre-hydration — re-emit once the
      // store's cache actually reflects any previously-saved bookings.
      void this.store.init().then(() => this._bookings$.next(this.read()));
    }
    // Best-effort, fire-and-forget real-reservation hydration whenever a real
    // (backend-linked) user is signed in — mirrors Phase 3's my-rv.util.ts
    // pattern. Never awaited by anything on the critical path.
    this.auth.currentUser$.subscribe(user => {
      if (user?.id) void this.refreshRealBookings(user.id);
      else this._realBookings$.next([]);
    });
    this.auth.loggedOut$.subscribe(() => this.write([]));
  }

  /** All bookings for one user, newest first. */
  /** Every booking the current device has — drives reveal-aware aggregations
   * that need to look up bookings by id across users. */
  getAll(): IBooking[] {
    return [...this._bookings$.value, ...this._realBookings$.value];
  }

  list(userEmail: string): IBooking[] {
    return this.getAll()
      .filter(b => b.userEmail === userEmail)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  getById(id: string): IBooking | null {
    if (id.startsWith('res-')) return this._realBookings$.value.find(b => b.id === id) ?? null;
    return this.read().find(b => b.id === id) ?? null;
  }

  /**
   * True if any *active* (not cancelled/declined) booking the user already has at this listing
   * overlaps the given date range. Used by the review page to block double-booking.
   *
   * NOTE: per-user only. Cross-user conflicts (two RVers booking the same listing for overlapping
   * dates) need backend coordination — see plan risks.
   */
  hasUserDateConflict(
    userEmail: string,
    listingId: number,
    range: { start: string; end: string },
  ): boolean {
    // Compare local calendar days, not instants: real bookings carry bare
    // `YYYY-MM-DD` while mock bookings and the review page's range carry full
    // ISO timestamps, and mixing the two skews by the viewer's UTC offset.
    const dayKey = (iso: string): string | null => {
      const d = parseBookingDate(iso);
      return d ? isoKey(d) : null;
    };
    const start = dayKey(range.start);
    const end = dayKey(range.end);
    if (!start || !end) return false;
    return this.list(userEmail).some(b => {
      if (b.status === 'cancelled' || b.status === 'declined') return false;
      if (b.listingId !== listingId) return false;
      const bStart = dayKey(b.dates.start);
      const bEnd = dayKey(b.dates.end);
      if (!bStart || !bEnd) return false;
      // [start, end) ranges overlap when A.start < B.end && B.start < A.end,
      // so checking in on another stay's checkout day is not a conflict.
      return start < bEnd && bStart < end;
    });
  }

  /**
   * Create a booking. When the caller has already resolved a real `ISite.id` to book
   * against (`input.realSiteId` — set by `booking-review.component.ts` from the
   * listing's real `sites[]`, not just self-published listings) it's used directly.
   * Otherwise this falls back to the publish-time snapshot (`realSiteId` written only
   * for listings published from this browser) so the host's own preview/self-publish
   * flow keeps working unchanged. Everything else keeps the original localStorage mock
   * behavior. Throws on real-path failure (payment declined, site unavailable, etc.) so
   * the caller can surface a real error instead of silently falling back to mock.
   */
  async createBooking(input: Omit<IBooking, 'id' | 'createdAt'>): Promise<IBooking> {
    const user = this.auth.currentUser;
    const realSiteId = input.realSiteId ?? readPublishedSnapshot(input.listingId)?.realSiteId;
    if (realSiteId && user?.id) {
      return this.createRealBooking(input, realSiteId);
    }
    return this.createMockBooking(input);
  }

  private createMockBooking(input: Omit<IBooking, 'id' | 'createdAt'>): IBooking {
    const id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : `b-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const booking: IBooking = {
      ...input,
      id,
      createdAt: new Date().toISOString(),
    };
    const all = this.read();
    all.push(booking);
    this.write(all);
    return booking;
  }

  private async createRealBooking(input: Omit<IBooking, 'id' | 'createdAt'>, siteId: number): Promise<IBooking> {
    const rvId = getActiveRvProfile(this.platformId)?.id;
    const payload = buildReservationCreatePayload({
      siteId,
      startIso: input.dates.start,
      endIso: input.dates.end,
      rvId,
      addOns: input.addOns,
    });
    const created = await firstValueFrom(this.reservationSvc.create(payload));
    if (!created.id) throw new Error('Server did not return a reservation id.');
    const booking = toBookingView(created);
    this.upsertRealBooking(booking);
    return booking;
  }

  async cancel(id: string, reason?: string): Promise<IBooking | null> {
    if (id.startsWith('res-')) {
      const booking = await this.cancelReal(id, reason);
      return booking;
    }
    return this.cancelMock(id, reason);
  }

  private cancelMock(id: string, reason?: string): IBooking | null {
    const all = this.read();
    const idx = all.findIndex(b => b.id === id);
    if (idx === -1) return null;
    const cleaned = reason?.trim();
    all[idx] = {
      ...all[idx],
      status: 'cancelled',
      cancelReason: cleaned || undefined,
    };
    this.write(all);
    return all[idx];
  }

  private async cancelReal(id: string, reason?: string): Promise<IBooking | null> {
    const current = this.realBookingById(id);
    if (!current?.realReservationId) return null;
    const updated = await firstValueFrom(this.reservationSvc.cancel(current.realReservationId, reason?.trim() || ''));
    const booking = toBookingView(updated);
    this.upsertRealBooking(booking);
    return booking;
  }

  /**
   * Bookings that qualify a user to earn credit — completed, confirmed/approved,
   * reviewed, and the FIRST reviewed booking per listing (anti-fraud lite — repeat
   * stays at the same listing earn once).
   *
   * Mock-only — see class doc comment for why real bookings don't feed this.
   */
  private earningBookings(userEmail: string): IBooking[] {
    const now = Date.now();
    const reviewed = this.read()
      .filter(b => b.userEmail === userEmail
                && (b.status === 'confirmed' || b.status === 'approved')
                && new Date(b.dates.end).getTime() < now
                && !!b.reviewedAt)
      .sort((a, b) => (a.reviewedAt || '').localeCompare(b.reviewedAt || ''));
    const seenListings = new Set<number>();
    const earners: IBooking[] = [];
    for (const b of reviewed) {
      if (seenListings.has(b.listingId)) continue;
      seenListings.add(b.listingId);
      earners.push(b);
    }
    return earners;
  }

  /** True when this booking is the user's first reviewed stay at that listing. */
  qualifiesForCredit(booking: IBooking): boolean {
    return this.earningBookings(booking.userEmail).some(b => b.id === booking.id);
  }

  /**
   * Available reward credit for a user, in dollars. Earned = nights from each
   * qualifying booking × REVIEW_CREDIT_PER_NIGHT. Spent = creditApplied across all
   * bookings. Always non-negative.
   */
  getAvailableCredit(userEmail: string): number {
    const earnedNights = this.earningBookings(userEmail)
      .reduce((sum, b) => sum + (b.nights || 0), 0);
    const earned = earnedNights * REVIEW_CREDIT_PER_NIGHT;
    const spent = this.read()
      .filter(b => b.userEmail === userEmail)
      .reduce((sum, b) => sum + (b.creditApplied || 0), 0);
    return Math.max(0, earned - spent);
  }

  /** Chronological credit ledger — earned + spent entries, newest first. */
  getCreditHistory(userEmail: string): ICreditEntry[] {
    const entries: ICreditEntry[] = [];
    for (const b of this.earningBookings(userEmail)) {
      entries.push({
        type: 'earned',
        amount: (b.nights || 0) * REVIEW_CREDIT_PER_NIGHT,
        bookingId: b.id,
        listingTitle: b.listingTitle,
        date: b.reviewedAt || b.dates.end,
      });
    }
    for (const b of this.read().filter(x => x.userEmail === userEmail && (x.creditApplied || 0) > 0)) {
      entries.push({
        type: 'spent',
        amount: b.creditApplied || 0,
        bookingId: b.id,
        listingTitle: b.listingTitle,
        date: b.createdAt,
      });
    }
    return entries.sort((a, b) => b.date.localeCompare(a.date));
  }

  /** Flag a completed booking as reviewed. Called after a successful Review save.
   *  Mock-only (see class doc comment) — no-op for real bookings. */
  markReviewed(id: string): IBooking | null {
    if (id.startsWith('res-')) return this.realBookingById(id);
    const all = this.read();
    const idx = all.findIndex(b => b.id === id);
    if (idx === -1) return null;
    all[idx] = { ...all[idx], reviewedAt: new Date().toISOString() };
    this.write(all);
    return all[idx];
  }

  /** Flag a completed booking as host-reviewed. Mirrors markReviewed for the
   * host → guest direction; drives the two-sided reveal, and (for real
   * bookings) is what makes a reviewed booking actually leave the host
   * dashboard's "Review your guests" list — call this after a successful
   * review/ban submit. */
  markHostReviewed(id: string): IBooking | null {
    if (id.startsWith('res-')) {
      const current = this.realBookingById(id);
      if (!current) return null;
      const updated = { ...current, hostReviewedAt: new Date().toISOString() };
      this.upsertRealBooking(updated);
      return updated;
    }
    const all = this.read();
    const idx = all.findIndex(b => b.id === id);
    if (idx === -1) return null;
    all[idx] = { ...all[idx], hostReviewedAt: new Date().toISOString() };
    this.write(all);
    return all[idx];
  }

  /** Host-initiated decision on a pending request — approve or decline. */
  async hostDecide(id: string, decision: 'approved' | 'declined', reason?: string): Promise<IBooking | null> {
    if (id.startsWith('res-')) return this.hostDecideReal(id, decision, reason);
    return this.hostDecideMock(id, decision, reason);
  }

  private hostDecideMock(id: string, decision: 'approved' | 'declined', reason?: string): IBooking | null {
    const all = this.read();
    const idx = all.findIndex(b => b.id === id);
    if (idx === -1) return null;
    if (all[idx].status !== 'pending') return null;
    const cleaned = reason?.trim();
    all[idx] = {
      ...all[idx],
      status: decision,
      cancelReason: decision === 'declined' ? (cleaned || undefined) : all[idx].cancelReason,
    };
    this.write(all);
    if (decision === 'approved') {
      this.toasts.success(`Approved — ${all[idx].listingTitle} is locked in for the guest.`);
    } else {
      this.toasts.info('Request declined.');
    }
    return all[idx];
  }

  private async hostDecideReal(id: string, decision: 'approved' | 'declined', reason?: string): Promise<IBooking | null> {
    const current = this.realBookingById(id);
    if (!current?.realReservationId) return null;
    const cleaned = reason?.trim() || '';
    const call$ = decision === 'approved'
      ? this.reservationSvc.accept(current.realReservationId, cleaned)
      : this.reservationSvc.reject(current.realReservationId, cleaned);
    const updated = await firstValueFrom(call$);
    const booking = toBookingView(updated);
    this.upsertRealBooking(booking);
    if (current.changeRequested) {
      if (decision === 'approved') {
        this.toasts.success('Change approved — the guest\'s new dates are booked.');
      } else {
        this.toasts.info('Change declined — the original booking stands.');
      }
    } else if (decision === 'approved') {
      this.toasts.success(`Approved — ${booking.listingTitle} is locked in for the guest.`);
    } else {
      this.toasts.info('Request declined.');
    }
    return booking;
  }

  /** Host-initiated cancellation on an already-approved/confirmed booking. */
  async hostCancel(id: string, reason?: string): Promise<IBooking | null> {
    if (id.startsWith('res-')) {
      const booking = await this.cancelReal(id, reason);
      if (booking) this.toasts.info('Reservation cancelled.');
      return booking;
    }
    return this.hostCancelMock(id, reason);
  }

  private hostCancelMock(id: string, reason?: string): IBooking | null {
    const all = this.read();
    const idx = all.findIndex(b => b.id === id);
    if (idx === -1) return null;
    const status = all[idx].status;
    if (status !== 'approved' && status !== 'confirmed') return null;
    const cleaned = reason?.trim();
    all[idx] = {
      ...all[idx],
      status: 'cancelled',
      cancelReason: cleaned || undefined,
    };
    this.write(all);
    this.toasts.info('Reservation cancelled.');
    return all[idx];
  }

  /**
   * Modify an existing booking's dates and/or guest count. Recomputes nights,
   * subtotal, and total from the original pricePerNight. Returns null if the
   * booking is missing, cancelled/declined, or check-in has already passed.
   *
   * Real bookings: `guests` has no backend equivalent (see class doc comment)
   * and is silently dropped — dates and add-ons (extras) do change
   * server-side, re-priced via `PUT /reservation/:id/change`.
   */
  async modify(
    id: string,
    patch: { start?: string; end?: string; guests?: number; addOns?: IBookingAddOn[] },
  ): Promise<IBooking | null> {
    if (id.startsWith('res-')) return this.modifyReal(id, patch);
    return this.modifyMock(id, patch);
  }

  private modifyMock(
    id: string,
    patch: { start?: string; end?: string; guests?: number; addOns?: IBookingAddOn[] },
  ): IBooking | null {
    const all = this.read();
    const idx = all.findIndex(b => b.id === id);
    if (idx === -1) return null;
    const current = all[idx];
    if (current.status === 'cancelled' || current.status === 'declined') return null;
    if (new Date(current.dates.start).getTime() < Date.now()) return null;

    const startIso = patch.start ?? current.dates.start;
    const endIso   = patch.end   ?? current.dates.end;
    const guests   = typeof patch.guests === 'number' ? Math.max(1, patch.guests) : current.guests;
    const addOns   = patch.addOns ?? current.addOns ?? [];

    const startMs = new Date(startIso).getTime();
    const endMs   = new Date(endIso).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;

    const nights      = Math.max(1, Math.round((endMs - startMs) / 86_400_000));
    const subtotal    = roundMoney(current.pricePerNight * nights);
    const addOnsTotal = roundMoney(addOns.reduce((sum, a) => sum + a.amount, 0));
    // All-in pricing: pricePerNight is already post-fee, so cleaningFee/serviceFee
    // (both always 0 now) are never added on top of the recomputed subtotal.
    const total       = roundMoney(subtotal + addOnsTotal);

    all[idx] = {
      ...current,
      dates: { start: startIso, end: endIso },
      guests,
      nights,
      subtotal,
      addOns: addOns.length > 0 ? addOns : undefined,
      addOnsTotal: addOnsTotal || undefined,
      total,
      modifiedAt: new Date().toISOString(),
    };
    this.write(all);
    return all[idx];
  }

  private async modifyReal(
    id: string,
    patch: { start?: string; end?: string; guests?: number; addOns?: IBookingAddOn[] },
  ): Promise<IBooking | null> {
    const current = this.realBookingById(id);
    if (!current?.realReservationId || !current.realSiteId) return null;
    const startIso = patch.start ?? current.dates.start;
    const endIso = patch.end ?? current.dates.end;
    const order: IOrder = {
      siteId: current.realSiteId,
      date: isoRangeToCDate(startIso, endIso),
      extras: addOnsToOrderExtras(patch.addOns ?? current.addOns),
      // Keep the booked rig — a date change shouldn't swap in whichever RV is active now.
      rvId: current.realRvId ?? getActiveRvProfile(this.platformId)?.id,
    };
    const updated = await firstValueFrom(this.reservationSvc.change(current.realReservationId, order));
    const booking = toBookingView(updated);
    this.upsertRealBooking(booking);
    return booking;
  }

  /**
   * Server-priced quote for a proposed change to a real booking — the same
   * pricing `PUT /reservation/:id/change` will apply. Null for mock bookings
   * (they reprice locally in `modify`).
   */
  async quoteModify(
    id: string,
    patch: { start: string; end: string; addOns?: IBookingAddOn[] },
  ): Promise<ModifyQuote | null> {
    const current = this.realBookingById(id);
    if (!current?.realSiteId) return null;
    const quoted = await firstValueFrom(this.reservationSvc.quote({
      site: { id: current.realSiteId },
      date: isoRangeToCDate(patch.start, patch.end),
      extras: addOnsToReservationExtras(patch.addOns),
    }));
    const summary = priceSummaryFrom(new PriceDisplay(quoted.priceComponents ?? []));
    return { ...summary, difference: roundMoney(summary.total - current.total) };
  }

  // ---- Real-reservation cache -------------------------------------------

  private realBookingById(id: string): IBooking | null {
    return this._realBookings$.value.find(b => b.id === id) ?? null;
  }

  private upsertRealBooking(booking: IBooking): void {
    const next = this._realBookings$.value.filter(b => b.id !== booking.id);
    next.push(booking);
    this._realBookings$.next(next);
  }

  /** Fetch this user's real reservations, both as guest (bookings they made)
   *  and as host (bookings on listings they own), and cache the merged,
   *  mapped result. Best-effort — a failure here leaves whatever was already
   *  cached in place rather than clearing it. Called once automatically on
   *  login (see the constructor's currentUser$ subscription), and callable
   *  again by pages that need up-to-date real bookings without waiting for
   *  the next login — e.g. the inbox and host-dashboard re-call this on
   *  load, since there's no polling loop for real bookings the way there is
   *  for message threads. */
  async refreshRealBookings(userId: number): Promise<void> {
    try {
      const [asGuest, asHost] = await Promise.all([
        firstValueFrom(this.reservationSvc.fetchForGuest(userId)),
        firstValueFrom(this.reservationSvc.fetchForHost(userId)),
      ]);
      const byId = new Map<number, IBooking>();
      for (const r of [...asGuest, ...asHost]) {
        if (!r.id) continue;
        byId.set(r.id, toBookingView(r));
      }
      this._realBookings$.next([...byId.values()]);
    } catch (err) {
      console.error('[booking] failed to refresh real reservations', err);
    }
  }

  // ---- Storage (mock bookings only) -------------------------------------

  private read(): IBooking[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    return this.store.read(BOOKINGS_IDB_KEY) ?? [];
  }

  private write(bookings: IBooking[]): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Previously an unguarded localStorage.setItem with no try/catch —
    // store.write()'s rejection (storage full/unavailable) is now caught
    // instead of becoming an unhandled promise rejection.
    this.store.write(BOOKINGS_IDB_KEY, bookings).catch(() => { /* quota / unavailable */ });
    this._bookings$.next(bookings);
  }
}
